import React, { useState, useRef, useEffect, forwardRef } from 'react';
import Toast from '../../Toast/Toast';
import AWS from 'aws-sdk';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../../../../application/config/config';
import Resizer from 'react-image-file-resizer';
import imageCompression from 'browser-image-compression';
import { openEditor } from 'react-profile';
import "react-profile/themes/default.min.css";
import "./custom-react-profile.css";
import CustomModal from '../../Modal/CustomModal';

AWS.config.update({
  accessKeyId: config.bucketS3.accessKeyId,
  secretAccessKey: config.bucketS3.secretAccessKey,
  region: config.bucketS3.region,
});

const FileUploadS3 = forwardRef((props, ref) => {

  const [value, setValue] = useState('');
  const [file, setFile] = useState(props.file ? props.file : '');
  const [fileExtension, setFileExtension] = useState(
    props.file ? props.file.split('.').pop() : ''
  );
  const [isImage, setIsImage] = useState(
    props.file
      ? ['jpeg', 'jpg', 'png', 'ico'].includes(props.file.split('.').pop().toLowerCase())
      : false
  );
  const [fileKey, setFileKey] = useState('');
  const [link, setLink] = useState(props.file);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const extensionImages = ['jpeg', 'jpg', 'png', 'ico'];
  const extensionVideos = ['mp4', 'mov', 'avi'];
  const limitSize = props.limitFileSize ? props.limitFileSize : 10;

  const uploadRef = useRef(null);

  useEffect(() => {
    if (uploadRef.current) {
      const progress = Math.round((uploadRef.current.bytesUploaded / uploadRef.current.contentLength) * 100);
      setUploadProgress(progress);
    }
  }, []);

  const [showProgress, setShowProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const [showAttachButton, setShowAttachButton] = useState(false);

  const resizeFile = (file) => new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();

    let format;
    switch (fileExtension) {
      case 'png':
        format = 'PNG';
        break;
      case 'jpeg':
      case 'jpg':
        format = 'JPEG';
        break;
      case 'ico':
        format = 'ICO';
        break;
      default:
        format = 'JPEG';
    }

    let quality = 90;
    if (file.size > 1024 * 1024) {
      quality = 70;
    } else if (file.size > 2 * 1024 * 1024) {
      quality = 50;
    }

    const url = URL.createObjectURL(file);
    let img = new Image();
    img.onload = () => {
      if (format === 'JPEG') {
        Resizer.imageFileResizer(
          file,
          img.width,
          img.height,
          format,
          quality,
          0,
          (uri) => {
            resolve(uri);
          },
          'blob'
        );
      } else {
        compressPNG(file).then(compressedFile => {
          resolve(compressedFile);
        }).catch(error => {
          reject(error);
        });
      }

      URL.revokeObjectURL(url);
    };

    img.onerror = (error) => {
      reject(error);
    };
    img.src = url;
  });

  const compressPNG = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/png'
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleCrop = async (src, fileUrl, fileName) => {
    const result = await openEditor({
      src,
      square: true,
      modules: ["crop"],
    });

    if (result.done) {
      const params = {
        Bucket: config.bucketS3.bucket,
        Key: fileUrl,
        Body: await result.editedImage.getBlob(),
      };

      const s3CropUpload = new AWS.S3().upload(params);

      s3CropUpload.on('httpUploadProgress', (progress) => {
        const uploadProgress = Math.round((progress.loaded / progress.total) * 100);
        setUploadProgress(uploadProgress);
      });

      s3CropUpload.send(async (err, data) => {
        if (err) {
          Toast.error('Erro ao fazer o upload do arquivo.');
        } else {
          onUpload(props.name, data.Location);
          setValue('');
          setFile(fileName);
          setFileExtension(fileExtension);
          setIsImage(true);
          setLink(data.Location);
        }
        setShowProgress(false);
      });

      setUploadProgress(0);
      setUploadCancelled(false);
      setShowProgress(true);
      uploadRef.current = s3CropUpload;
    }
  };

  const handleChange = async (event) => {
    if (props.disabled) {
      Toast.error(`Upload de arquivo desabilitado.`);
      return;
    } else if (event.target.files.length > 0) {
      const size = event.target.files[0].size / (1024 * 1024);
      if (size > limitSize) {
        Toast.error(`O arquivo não pode ser maior que ${limitSize} MB(s).`);
        return;
      }

      let file = event.target.files[0];
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop();
      const isImage = extensionImages.includes(fileExtension.toLowerCase());
      const isVideo = extensionVideos.includes(fileExtension.toLowerCase());

      if (isVideo) {
        const videoDuration = await getVideoDuration(file);
        if (videoDuration > props.maxDuration) {
          Toast.error('O vídeo não pode ser maior que 1 minuto.');
          return;
        }
      }

      if (isImage && props.imageCompress !== false) {
        try {
          file = await resizeFile(file);
        } catch (err) {
          Toast.error('Erro ao redimensionar a imagem.');
          return;
        }
      }

      const currentDate = moment().format('YYYY/MM/DD');
      const uniqueId = uuidv4();
      let fileUrl = `${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;

      if (config.bucketS3 && config.bucketS3.path_default && config.bucketS3.path_default != '') {
        fileUrl = `${config.bucketS3.path_default}/${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;
      } else {
        Toast.error('Erro ao fazer o upload do arquivo. É necessário informar o path_default nas configurações.');
        return;
      }

      fileUrl = fileUrl.replace('//', '/');

      setFileKey(fileUrl)

      const params = {
        Bucket: config.bucketS3.bucket,
        Key: fileUrl,
        Body: file,
      };

      beforeUpload();
      const s3Upload = new AWS.S3().upload(params);

      s3Upload.on('httpUploadProgress', (progress) => {
        const uploadProgress = Math.round((progress.loaded / progress.total) * 100);
        setUploadProgress(uploadProgress);
      });

      s3Upload.send(async (err, data) => {
        if (err) {
          Toast.error('Erro ao fazer o upload do arquivo.');
        } else {
          onUpload(props.name, data.Location);
          setValue('');
          setFile(fileName);
          setFileExtension(fileExtension);
          setIsImage(isImage);
          setLink(data.Location);

          if (props.crop && isImage) {
            handleCrop(data.Location, fileUrl, fileName);
          }
        }
        setShowProgress(false);
      });

      setUploadProgress(0);
      setUploadCancelled(false);
      setShowProgress(true);
      uploadRef.current = s3Upload;
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      video.src = url;
    });
  };

  const onUpload = (inputName, file) => {
    props.onUpload(inputName, file);
  };

  const beforeUpload = () => {
    props.beforeUpload();
  };

  const deleteFile = () => {
    const s3 = new AWS.S3();

    const params = {
      Bucket: config.bucketS3.bucket,
      Key: link
    };

    s3.deleteObject(params, (error, data) => {
      if (error) {
        Toast.error('Erro ao excluir o arquivo.');
      } else {
        setShowAttachButton(false);
        setLink('');
        setFile('');
        setShowProgress(false);
        props.onRemove(props.name, '');
      }
    });
  };

  const clickRemove = () => {
    deleteFile();
  };

  const cancelUpload = () => {
    if (uploadRef.current) {
      uploadRef.current.abort();
      setUploadCancelled(true);
      setShowProgress(false);
      setShowAttachButton(false);
      setLink('');
    }
  };

  return (
    <div style={props.style ? props.style : { marginTop: 10 }} ref={ref}>
      {!file && !props.disabled && (
        <div>
          <label className="btn btn-primary">
            <input
              id="my-file-selector"
              className="d-none"
              type="file"
              name={props.name}
              onChange={handleChange}
              accept={props.accept}
              value={value}
              autoFocus={props.autoFocus ? true : false}
            />
            {props.titleButton}
          </label>
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}

      {showProgress && (
        <div>
          <div>
            <progress value={uploadProgress} max="100" />
          </div>
          {!uploadCancelled && (
            <div>
              <button onClick={cancelUpload}>Cancelar</button>
            </div>
          )}
        </div>
      )}
      {file && !isImage && !extensionVideos.includes(fileExtension.toLowerCase()) && (
        <div>
          <a target="_blank" href={link} rel="noreferrer">
            Download
          </a>
          &nbsp;&nbsp;
          <span onClick={props.confirmRemove ? () => setOpenConfirmationModal(true) : () => clickRemove()} className="required" style={{ cursor: 'pointer' }}>
            <strong>X</strong>
          </span>
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}
      {file && isImage && (
        <div style={{ width: '100px' }}>
          <a target="_blank" href={link} rel="noreferrer">
            <img style={{ width: '100px' }} src={link} alt="uploaded" />
          </a>
          {!props.disabled && (
            <div onClick={props.confirmRemove ? () => setOpenConfirmationModal(true) : () => clickRemove()} className="required" style={{ cursor: 'pointer', textAlign: 'center', marginTop: '5px' }}>
              remover
            </div>
          )}
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}
      {file && extensionVideos.includes(fileExtension.toLowerCase()) && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <video width="420" height="340" controls>
            <source src={link} type={`video/${fileExtension}`} />
            Your browser does not support the video tag.
          </video>
          {!props.disabled && (
            <div onClick={props.confirmRemove ? () => setOpenConfirmationModal(true) : () => clickRemove()} className="btn btn-sm btn-danger" style={{ cursor: 'pointer', textAlign: 'center', marginTop: '5px', maxWidth: '420px' }}>
              <i className='fas fa-trash mr-2' />
              Remover
            </div>
          )}
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}
      <CustomModal 
        title="Excluir arquivo"
        show={openConfirmationModal}
        handleClose={() => setOpenConfirmationModal(false)} 
        content={<>Tem certeza de que deseja excluir esse arquivo?</>}
        handleConfirm={(e) => {
          e.preventDefault();
          clickRemove();
          setOpenConfirmationModal(false);
        }}
        handleCancel={() => setOpenConfirmationModal(false)}
      />
    </div>
  );
});

FileUploadS3.defaultProps = {
  onChange: () => { },
  onUpload: () => { },
  beforeUpload: () => { },
  path: '',
  accept: 'image/*,video/*',
  titleButton: 'Anexar',
  file: '',
  limitFileSize: 300,
  disabled: false,
  imageCompress: true,
  maxDuration: 60,
  confirmRemove: false
};

export default FileUploadS3;
